import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import {
  BENCHMARK_LABEL_PADRAO_ESCRITORIO,
  BENCHMARK_ORIGEM,
} from '@modules/crm/constants/GrupoFamiliar'
import { gruposFamiliares } from '@modules/crm/constants/URL'
import { apiSettingsBenchmarks } from '@modules/settings/api/apiSettingsBenchmarks'
// import mockGrupos from '@modules/crm/mocks/crmGruposFamiliares'
// import mockGruposCliente from '@modules/crm/mocks/crmGruposFamiliaresCliente'

class ApiCrmGrupoFamiliar extends APITemplate {
  constructor() {
    super()
    this.reqCarregarGrupos = null
    this.reqCarregarListagem = null
  }

  normalizeGrupo(grupo) {
    if (!grupo || typeof grupo !== 'object') {
      return {}
    }

    const nome = grupo.grupo_familiar_nome || ''
    const descricao = grupo.descricao || ''

    let fullName = nome
    if (descricao && descricao.length) {
      fullName = `${nome} - ${descricao}`
    }

    return {
      ...grupo,
      fullName,
      benchmarkPadraoEscritorio: this.isBenchmarkPadraoEscritorio(grupo),
      benchmarkLabel: this.resolveBenchmarkLabel(grupo),
    }
  }

  /**
   * Sem fórmula e sem indexador = grupo sem benchmark próprio; a comparação é
   * contra o CDI. Quando o backend informa `benchmark_origem`, ele é a fonte —
   * os dois campos só entram como fallback nas rotas que ainda não o devolvem.
   */
  isBenchmarkPadraoEscritorio(grupo = {}) {
    if (grupo.benchmark_origem) {
      return grupo.benchmark_origem === BENCHMARK_ORIGEM.ESCRITORIO_DEFAULT
    }

    return !grupo.benchmark_personalizado && !grupo.id_benchmark
  }

  /**
   * Rótulo pronto para exibição, para que a tela não precise decidir entre nome
   * do benchmark e texto de padrão dentro do template.
   */
  resolveBenchmarkLabel(grupo = {}) {
    if (this.isBenchmarkPadraoEscritorio(grupo)) {
      return BENCHMARK_LABEL_PADRAO_ESCRITORIO
    }

    return grupo.benchmark_nome || BENCHMARK_LABEL_PADRAO_ESCRITORIO
  }

  normalizeGrupos(lista) {
    if (!Array.isArray(lista)) {
      return []
    }

    return lista.map((grupo) => this.normalizeGrupo(grupo))
  }

  // eslint-disable-next-line max-statements
  async carregar({ forceUpdate = false } = {}) {
    if (this.reqCarregarGrupos) {
      return this.reqCarregarGrupos
    }

    const lastUpdate = store.getters['crm/gruposFamiliaresLastUpdate']

    if (!forceUpdate) {
      if (!diffTimeMinutes(lastUpdate, MINUTES_TIMEOUT_REQUESTS)) {
        return {
          status: 200,
          value: store.getters['crm/gruposFamiliares'],
        }
      }
    }

    if (store.getters['crm/gruposFamiliaresLoading']) {
      return {
        status: 200,
        value: store.getters['crm/gruposFamiliares'],
      }
    }

    store.dispatch('crm/setGruposFamiliaresLoading', true)

    this.reqCarregarGrupos = con.get(gruposFamiliares.manage)
    const response = await this.reqCarregarGrupos
    // const response = mockGrupos
    if (response && response.status === 200) {
      const lista = this.normalizeGrupos(response.value || [])
      store.dispatch('crm/setGruposFamiliares', lista)
      response.value = lista
    } else {
      store.dispatch('crm/setGruposFamiliares', [])
    }

    store.dispatch('crm/setGruposFamiliaresLoading', false)
    this.reqCarregarGrupos = null

    return response || {}
  }

  /**
   * Cópia profunda do que está na store — o array vivo do Vuex nunca sai daqui,
   * senão qualquer consumidor consegue mutá-lo sem passar por uma mutation.
   */
  cloneListagem() {
    return structuredClone(store.getters['crm/gruposFamiliaresListagem'] || [])
  }

  /**
   * O cache da listagem ainda vale? `lastUpdate` nulo significa que a tela
   * nunca carregou nesta sessão.
   */
  listagemEmCache() {
    const lastUpdate = store.getters['crm/gruposFamiliaresListagemLastUpdate']
    return Boolean(
      lastUpdate && !diffTimeMinutes(lastUpdate, MINUTES_TIMEOUT_REQUESTS)
    )
  }

  /**
   * Guarda a lista na store e troca o envelope `{meta, grupos}` pelo array já
   * normalizado — a tela consome `value` como lista, não como envelope.
   */
  persistListagem(response) {
    const value = (response && response.value) || {}
    const lista = this.normalizeGrupos(value.grupos || [])
    store.dispatch('crm/setGruposFamiliaresListagem', lista)
    return { ...response, value: lista }
  }

  /**
   * Listagem da tela de grupos familiares: grupos + membros visíveis +
   * contagens de escopo em uma requisição. O backend devolve
   * `{ meta, grupos }`; guardamos apenas `grupos` na store e é isso que o
   * `value` da resposta carrega.
   *
   * @param {Object} args
   * @param {Boolean} [args.forceUpdate] Ignora o cache por tempo.
   * @returns {Promise<Object>} `{status, message, value: Array}`.
   */
  async carregarListagem({ forceUpdate = false } = {}) {
    if (this.reqCarregarListagem) {
      return this.reqCarregarListagem
    }

    if (!forceUpdate && this.listagemEmCache()) {
      return this.buildSuccessResponse({ value: this.cloneListagem() })
    }

    store.dispatch('crm/setGruposFamiliaresListagemLoading', true)

    this.reqCarregarListagem = con.get(gruposFamiliares.listagem)
    const response = await this.reqCarregarListagem
    this.reqCarregarListagem = null

    const sucesso = response && response.status === 200
    const resultado = sucesso
      ? this.persistListagem(response)
      : this.resolveFalhaListagem(response)

    store.dispatch('crm/setGruposFamiliaresListagemLoading', false)

    return resultado
  }

  /**
   * Falha na carga: a store fica vazia (nunca com dado velho travado na tela) e
   * a mensagem devolvida diz o que fazer em seguida.
   */
  resolveFalhaListagem(response) {
    store.dispatch('crm/setGruposFamiliaresListagem', [])

    return (
      response ||
      this.buildErrorResponse({
        message:
          'Não foi possível carregar os grupos familiares. Tente novamente em instantes ou contate o suporte.',
      })
    )
  }

  /**
   * Exclui o grupo. Os vínculos de clientes caem em cascata no backend; os
   * clientes em si não são tocados.
   *
   * @param {Object} args
   * @param {String} args.id UUID do grupo.
   * @returns {Promise<Object>} `{status, message, value}`.
   */
  async excluirGrupo({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        message:
          'Grupo familiar inválido. Recarregue a página e tente de novo.',
        status: 400,
        value: null,
      })
    }

    const response = await con.delet(gruposFamiliares.detalhe(id))

    return (
      response ||
      this.buildErrorResponse({
        message:
          'Não foi possível excluir o grupo familiar. Tente novamente mais tarde ou contate o suporte.',
        value: null,
      })
    )
  }

  buildGrupoPayload(grupo = {}) {
    return {
      grupo_familiar_nome: grupo.grupo_familiar_nome || '',
      descricao: grupo.descricao || '',
      // Sempre enviada, inclusive nula: é assim que o backend distingue
      // "removeu o benchmark do grupo" (volta ao CDI) de "não mexeu no campo".
      // É a FÓRMULA (v583) — "120% CDI", "IPC-A + 4,5%" —, o mesmo cadastro do
      // benchmark do cliente, e não mais o UUID de um indexador do escritório.
      benchmark_personalizado: grupo.benchmark_personalizado || null,
    }
  }

  async salvar({ grupo = {} } = {}) {
    const dado = this.buildGrupoPayload(grupo)

    const data = {
      data: JSON.stringify(dado),
    }

    let response = {}

    if (grupo.id) {
      response = await con.put(gruposFamiliares.detalhe(grupo.id), data)
    } else {
      response = await con.post(gruposFamiliares.manage, data)
    }

    if (response && response.status === 200) {
      const value = response.value[0] || response.value || {}
      const normalized = this.normalizeGrupo(value)
      store.dispatch('crm/addGruposFamiliares', normalized)
    }

    return (
      response || {
        status: 500,
        message: 'Erro ao salvar o grupo familiar.',
      }
    )
  }

  /**
   * Benchmarks que o grupo pode adotar — os do escritório mais os globais.
   *
   * Delega para `apiSettingsBenchmarks`, que já é dono dessa rota e do cache no
   * store de settings; duplicar a chamada aqui criaria um segundo cache do
   * mesmo dado. O que este método acrescenta é contrato de retorno: aquela
   * classe devolve o array cru quando serve do cache e o envelope quando vai à
   * rede, e a tela não deveria ter que lidar com os dois formatos.
   *
   * @param {Object} args
   * @param {Boolean} [args.forceUpdate] Ignora o cache de settings.
   * @returns {Promise<Object>} `{status, message, value: Array}`.
   */
  async carregarBenchmarks({ forceUpdate = false } = {}) {
    const response = await apiSettingsBenchmarks.carregar(forceUpdate)
    const lista = this.extractBenchmarks(response)

    if (Array.isArray(response)) {
      return this.buildSuccessResponse({
        message: 'Benchmarks carregados da memória',
        value: lista,
      })
    }

    if (response && response.status === 200) {
      return { ...response, value: lista }
    }

    return this.buildErrorResponse({
      message:
        'Não foi possível carregar os benchmarks do escritório. Tente novamente mais tarde ou contate o suporte.',
      value: [],
    })
  }

  extractBenchmarks(response) {
    if (Array.isArray(response)) {
      return response
    }

    if (response && Array.isArray(response.value)) {
      return response.value
    }

    return []
  }

  async carregarGruposCliente({ idClienteApp }) {
    if (idClienteApp === undefined || idClienteApp === null) {
      return {
        status: 400,
        message: 'Cliente inválido.',
      }
    }

    const response = await con.get(gruposFamiliares.clientes(idClienteApp))
    // const response = mockGruposCliente
    return (
      response || {
        status: 500,
        message: 'Erro ao carregar grupos familiares.',
      }
    )
  }

  async vincularGrupoCliente({ vinculo }) {
    if (!vinculo || typeof vinculo !== 'object') {
      return {
        status: 400,
        message: 'Dados do vínculo inválidos.',
      }
    }

    const { id, id_cliente_app, id_grupo_familiar, parentesco } = vinculo

    // `id` só entra no corpo quando existe de fato. O formulário inicializa
    // `id: ''`, e mandar string vazia numa coluna UUID faz o Postgres estourar
    // com `invalid input syntax for type uuid: ""` — HTTP 500 ao criar vínculo.
    // No POST o banco preenche o id via `uuid_generate_v4()`.
    const dado = {
      id_cliente_app,
      id_grupo_familiar,
      parentesco,
      ...(id ? { id } : {}),
    }

    const data = {
      data: JSON.stringify(dado),
    }

    let response = {}

    if (id) {
      response = await con.put(gruposFamiliares.clientes() + `/${id}`, data)
    } else {
      response = await con.post(gruposFamiliares.clientes(), data)
    }

    return (
      response || {
        status: 500,
        message: 'Erro ao vincular cliente ao grupo familiar.',
      }
    )
  }

  async removerVinculoGrupoCliente({ vinculoId }) {
    if (!vinculoId) {
      return {
        status: 400,
        message: 'Vínculo inválido.',
      }
    }

    const response = await con.delet(
      `${gruposFamiliares.clientes()}/${vinculoId}`
    )

    return (
      response || {
        status: 500,
        message: 'Erro ao excluir vínculo do grupo familiar.',
      }
    )
  }
}

const apiCrmGrupoFamiliar = new ApiCrmGrupoFamiliar()

export { apiCrmGrupoFamiliar }
